/**
 * App Configuration Data
 * 
 * Centralized app configuration data (achievements, themes, avatars)
 * extracted from App.jsx
 */

// Base achievements (not dependent on subjectAchievements)
export const baseAchievements = {
  first_steps: { id: 'first_steps', name: 'First Steps', description: 'Complete your first question!', icon: '👶', rarity: 'common', points: 10 },
  perfect_score: { id: 'perfect_score', name: 'Perfect Score', description: 'Get 100% on any subject!', icon: '💯', rarity: 'rare', points: 50 },
  subject_master: { id: 'subject_master', name: 'Subject Master', description: 'Complete all questions in a subject!', icon: '🎓', rarity: 'epic', points: 100 },
  speed_demon: { id: 'speed_demon', name: 'Speed Demon', description: 'Answer 10 questions in under 2 minutes!', icon: '⚡', rarity: 'rare', points: 75 },
  scholar: { id: 'scholar', name: 'Scholar', description: 'Complete 5 different subjects!', icon: '📚', rarity: 'epic', points: 150 },
  perfectionist: { id: 'perfectionist', name: 'Perfectionist', description: 'Get perfect scores on 3 subjects!', icon: '⭐', rarity: 'legendary', points: 200 },
  marathon_runner: { id: 'marathon_runner', name: 'Marathon Runner', description: 'Answer 50 questions in one session!', icon: '🏃', rarity: 'rare', points: 100 },
  early_bird: { id: 'early_bird', name: 'Early Bird', description: 'Play before 8 AM!', icon: '🌅', rarity: 'common', points: 25 },
  night_owl: { id: 'night_owl', name: 'Night Owl', description: 'Play after 8 PM!', icon: '🦉', rarity: 'common', points: 25 },
  streak_master: { id: 'streak_master', name: 'Streak Master', description: 'Play for 7 days in a row!', icon: '🔥', rarity: 'epic', points: 200 },
  artist: { id: 'artist', name: 'Artist', description: 'Complete the spelling practice!', icon: '🎨', rarity: 'common', points: 30 },
  scientist: { id: 'scientist', name: 'Scientist', description: 'Master all science questions!', icon: '🔬', rarity: 'rare', points: 80 },
  mathematician: { id: 'mathematician', name: 'Mathematician', description: 'Master all math questions!', icon: '🧮', rarity: 'rare', points: 80 },
  reader: { id: 'reader', name: 'Reader', description: 'Master all reading questions!', icon: '📖', rarity: 'rare', points: 80 },
  citizen: { id: 'citizen', name: 'Good Citizen', description: 'Master all citizenship questions!', icon: '🌟', rarity: 'rare', points: 80 },
  counter: { id: 'counter', name: 'Counter', description: 'Master all skip counting!', icon: '🔢', rarity: 'rare', points: 80 },
  phonics_master: { id: 'phonics_master', name: 'Phonics Master', description: 'Master all phonics questions!', icon: '📚', rarity: 'rare', points: 80 },
  grand_master: { id: 'grand_master', name: 'Grand Master', description: 'Complete ALL subjects with perfect scores!', icon: '👑', rarity: 'legendary', points: 500 }
};

// Export a function to get merged achievements - this avoids TDZ issues
// The merge will happen in the consuming code after all modules are loaded
export function getAchievements(subjectAchievements) {
  const subjectAchievementsFlat = Object.values(subjectAchievements || {}).flat().reduce((acc, achievement) => {
    acc[achievement.id] = achievement;
    return acc;
  }, {});
  return {
    ...baseAchievements,
    ...subjectAchievementsFlat
  };
}


export const themes = {
  default: { name: 'Default', colors: 'from-pink-200 via-purple-200 to-blue-200', unlocked: true },
  ocean: { name: 'Ocean', colors: 'from-blue-200 via-cyan-200 to-teal-200', unlocked: false, unlockRequirement: 'Complete 3 subjects' },
  forest: { name: 'Forest', colors: 'from-green-200 via-emerald-200 to-lime-200', unlocked: false, unlockRequirement: 'Get 5 perfect scores' },
  sunset: { name: 'Sunset', colors: 'from-orange-200 via-red-200 to-pink-200', unlocked: false, unlockRequirement: 'Play for 3 days in a row' },
  space: { name: 'Space', colors: 'from-purple-200 via-indigo-200 to-blue-200', unlocked: false, unlockRequirement: 'Answer 100 questions' },
  rainbow: { name: 'Rainbow', colors: 'from-pink-200 via-yellow-200 via-green-200 via-blue-200 to-purple-200', unlocked: false, unlockRequirement: 'Unlock all other themes' }
};

export const avatars = {
  // Kids avatars
  girl: { name: 'Girl', emoji: '👧', unlocked: true },
  boy: { name: 'Boy', emoji: '👦', unlocked: true },
  
  // Parent avatars
  dad: { name: 'Dad', emoji: '👨', unlocked: true },
  mom: { name: 'Mom', emoji: '👩', unlocked: true },
  
  // Fun avatars for everyone
  robot: { name: 'Robot', emoji: '🤖', unlocked: true },
  princess: { name: 'Princess', emoji: '👸', unlocked: true },
  prince: { name: 'Prince', emoji: '🤴', unlocked: true },
  superhero_girl: { name: 'Super Girl', emoji: '🦸‍♀️', unlocked: true },
  superhero_boy: { name: 'Super Boy', emoji: '🦸‍♂️', unlocked: true },
  astronaut_girl: { name: 'Astronaut Girl', emoji: '👩‍🚀', unlocked: true },
  astronaut_boy: { name: 'Astronaut Boy', emoji: '👨‍🚀', unlocked: true },
  teacher: { name: 'Teacher', emoji: '👨‍🏫', unlocked: true },
  scientist: { name: 'Scientist', emoji: '👨‍🔬', unlocked: true },
  artist: { name: 'Artist', emoji: '👨‍🎨', unlocked: true },
  unicorn: { name: 'Unicorn', emoji: '🦄', unlocked: true },
  dinosaur: { name: 'Dinosaur', emoji: '🦕', unlocked: true },
  
  // Keep default as girl for backwards compatibility
  default: { name: 'Girl', emoji: '👧', unlocked: true }
};

